// CRM Overview — Dashboard tab within project CRM
import { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Users, Building2, MessageSquare, Plus } from 'lucide-react'
import { getCrmStats, getCrmContacts, getCrmInteractions, getProject } from '../../../services/api'
import type { CrmStats, CrmContact, CrmInteraction } from '../../../types'
import PageHeader from '../../../components/ui/PageHeader'
import EmptyState from '../../../components/ui/EmptyState'
import CrmLoadError from '../../../components/crm/CrmLoadError'
import ContactName from '../../../components/crm/ContactName'
import { contactInitials } from '../../../utils/crmPipeline'
import { shortDate } from '../../../lib/shortDate'

export default function CrmOverview() {
  const { projectId } = useParams<{ projectId: string }>()
  const [stats, setStats] = useState<CrmStats | null>(null)
  const [recentContacts, setRecentContacts] = useState<CrmContact[]>([])
  const [recentInteractions, setRecentInteractions] = useState<CrmInteraction[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [projectName, setProjectName] = useState('')
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!projectId) return
    setLoading(true)
    setLoadError(false)
    // A failed request must show an error with Retry, never zero stats (UX-038).
    Promise.all([
      getCrmStats(projectId),
      getCrmContacts(projectId),
      getCrmInteractions(projectId),
    ]).then(([s, c, i]) => {
      setStats(s)
      setRecentContacts((c?.contacts || []).slice(0, 5))
      setRecentInteractions((i?.interactions || []).slice(0, 5))
    }).catch(() => setLoadError(true))
      .finally(() => setLoading(false))
  }, [projectId, reloadKey])

  // The project name is a heading nicety: if it can't load, the title stays "CRM".
  useEffect(() => {
    if (!projectId) return
    getProject(projectId).then((p) => setProjectName(p?.name || '')).catch(() => setProjectName(''))
  }, [projectId])

  if (loading) {
    return <div className="animate-pulse space-y-4"><div className="h-24 bg-sky-500/10 rounded-lg" /><div className="h-24 bg-sky-500/10 rounded-lg" /></div>
  }

  const title = projectName ? `CRM — ${projectName}` : 'CRM'

  if (loadError) {
    return (
      <div className="space-y-6">
        <PageHeader title={title} helpKey="crm" />
        <CrmLoadError what="this project's CRM" onRetry={() => setReloadKey((k) => k + 1)} />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title={title}
        description="People, organisations, and interactions for this project's innovation network."
        helpKey="crm"
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-sky-500/10 rounded-lg flex items-center justify-center">
            <Users size={20} className="text-sky-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats?.contacts ?? 0}</p>
            <p className="text-sm text-gray-500">Contacts</p>
          </div>
        </div>
        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center">
            <Building2 size={20} className="text-purple-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats?.organisations ?? 0}</p>
            <p className="text-sm text-gray-500">Organisations</p>
          </div>
        </div>
        <div className="light-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
            <MessageSquare size={20} className="text-green-600" />
          </div>
          <div>
            <p className="text-2xl font-bold text-gray-900">{stats?.interactions ?? 0}</p>
            <p className="text-sm text-gray-500">Interactions</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-3">
        <Link to={`/project/${projectId}/crm/contacts?add=1`} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white text-sm font-medium rounded-lg hover:bg-sky-600">
          <Plus size={16} /> Add Contact
        </Link>
        <Link to={`/project/${projectId}/crm/organisations?add=1`} className="flex items-center gap-2 px-4 py-2 bg-purple-500 text-white text-sm font-medium rounded-lg hover:bg-purple-600">
          <Plus size={16} /> Add Organisation
        </Link>
      </div>

      {/* Recent Contacts */}
      <div className="light-card">
        <div className="px-4 py-3 border-b border-sky-200/50 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">Recent Contacts</h3>
          <Link to={`/project/${projectId}/crm/contacts`} className="text-xs text-sky-600 hover:text-sky-700">View all</Link>
        </div>
        <div className="divide-y divide-sky-200/30">
          {recentContacts.length === 0 ? (
            <div className="p-4">
              <EmptyState
                compact
                icon={Users}
                title="No contacts yet"
                description="Add a contact or convert a paper author to start building this project's network."
              />
            </div>
          ) : (
            recentContacts.map((c) => (
              <Link key={c.contact_id} to={`/project/${projectId}/crm/contacts/${c.contact_id}`}
                className="flex items-center gap-3 px-4 py-3 hover:bg-white/30">
                <div className="w-8 h-8 bg-sky-500/10 rounded-full flex items-center justify-center text-xs font-semibold text-sky-700">
                  {contactInitials(c)}
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900"><ContactName contact={c} /></p>
                  {/* LIVE-DATA fix: never print "No details" — omit the
                      sub-line gracefully when there's genuinely nothing to show. */}
                  {(c.organisation_name || c.email) && (
                    <p className="text-xs text-gray-500">{c.organisation_name || c.email}</p>
                  )}
                </div>
                {c.source === 'author-conversion' && (
                  <span className="ml-auto text-xs bg-amber-500/20 text-amber-700 px-2 py-0.5 rounded-full">Author</span>
                )}
              </Link>
            ))
          )}
        </div>
      </div>

      {/* Recent Interactions */}
      <div className="light-card">
        <div className="px-4 py-3 border-b border-sky-200/50">
          <h3 className="text-sm font-semibold text-gray-900">Recent Interactions</h3>
        </div>
        <div className="divide-y divide-sky-200/30">
          {recentInteractions.length === 0 ? (
            <div className="p-4">
              <EmptyState
                compact
                icon={MessageSquare}
                title="No interactions yet"
                description="Logged calls, emails, and meetings with your contacts and organisations appear here."
              />
            </div>
          ) : (
            recentInteractions.map((i) => (
              <div key={i.interaction_id} className="px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs bg-sky-500/10 text-gray-600 px-2 py-0.5 rounded">{i.interaction_type}</span>
                  <p className="text-sm font-medium text-gray-900">{i.subject || 'No subject'}</p>
                </div>
                <p className="text-xs text-gray-500 mt-1">{shortDate(i.created_at)}</p>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  )
}
